import { HanamiElement, animationsDone, createShadow, define, prefersReducedMotion } from '../core/base';
import { deepActiveElement, getFocusable, lockScroll, unlockScroll } from '../core/focus';
import { t } from '../core/strings';
import { bringToastsToFront } from '../core/toast';

const css = `:host{--hn-dialog-width:32rem}:host([size="sm"]){--hn-dialog-width:24rem}:host([size="lg"]){--hn-dialog-width:44rem}:host([size="xl"]){--hn-dialog-width:60rem}*,*::before,*::after{box-sizing:border-box}dialog{width:min(var(--hn-dialog-width),calc(100vw - 2rem));max-width:none;max-height:calc(100dvh - 2rem);margin:auto;padding:0;overflow:visible;border:0;background:transparent;color:var(--hn-color-text)}dialog::backdrop{background:var(--hn-color-overlay);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);animation:fade-in var(--hn-duration-slow) var(--hn-ease-standard)}dialog.closing::backdrop{animation:fade-out var(--hn-duration-normal) var(--hn-ease-exit) forwards}.panel{display:flex;flex-direction:column;max-height:calc(100dvh - 2rem);overflow:hidden;border:1px solid var(--hn-color-border);border-radius:var(--hn-radius-2xl);background:var(--hn-color-surface-overlay);box-shadow:var(--hn-shadow-xl);font-family:var(--hn-font-body);animation:bloom-in var(--hn-duration-slow) var(--hn-ease-bloom)}dialog.closing .panel{animation:bloom-out var(--hn-duration-normal) var(--hn-ease-exit) forwards}dialog.shake .panel{animation:shake 360ms var(--hn-ease-standard)}.header{display:flex;align-items:flex-start;gap:var(--hn-space-4);padding:var(--hn-space-6) var(--hn-space-6) var(--hn-space-2)}.header ::slotted(*){flex:1;min-width:0}.title{flex:1;min-width:0;margin:0;color:var(--hn-color-text);font-family:var(--hn-font-display);font-size:var(--hn-text-2xl);font-weight:var(--hn-weight-bold);line-height:var(--hn-leading-snug);text-wrap:balance}.title:empty{display:none}.close{display:grid;place-items:center;flex-shrink:0;width:2.25rem;height:2.25rem;margin:-0.25rem -0.5rem 0 auto;padding:0;border:0;border-radius:var(--hn-radius-md);background:transparent;color:var(--hn-color-text-muted);cursor:pointer;transition:background-color var(--hn-duration-fast),color var(--hn-duration-fast),transform var(--hn-duration-normal) var(--hn-ease-bloom)}.close:hover{background:var(--hn-color-hover);color:var(--hn-color-text);transform:rotate(90deg)}.close:focus-visible{outline:none;box-shadow:var(--hn-focus-ring)}.close svg{width:1.25rem;height:1.25rem}:host([no-close-button]) .close{display:none}.body{flex:1;min-height:0;overflow-y:auto;padding:var(--hn-space-2) var(--hn-space-6) var(--hn-space-6);color:var(--hn-color-text-muted);line-height:var(--hn-leading-normal);overscroll-behavior:contain}.footer{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:var(--hn-space-2);padding:var(--hn-space-4) var(--hn-space-6);border-top:1px solid var(--hn-color-border);background:var(--hn-color-bg-subtle)}.footer.empty{display:none}:host([size="full"]) dialog{width:calc(100vw - 2rem);height:calc(100dvh - 2rem)}:host([size="full"]) .panel{height:100%}@media (max-width:640px){:host(:not([size="full"])) dialog{width:100vw;max-height:92dvh;margin:auto 0 0}:host(:not([size="full"])) .panel{max-height:92dvh;border-bottom:0;border-radius:var(--hn-radius-2xl) var(--hn-radius-2xl) 0 0;animation-name:sheet-in}:host(:not([size="full"])) dialog.closing .panel{animation-name:sheet-out}}:host([drawer]){--hn-dialog-width:26rem}:host([drawer]) dialog{width:min(var(--hn-dialog-width),100vw);height:100dvh;max-height:none;margin:0 0 0 auto}:host([drawer]) .panel{height:100%;max-height:none;border-width:0 0 0 1px;border-radius:var(--hn-radius-2xl) 0 0 var(--hn-radius-2xl);animation:slide-in-end var(--hn-duration-slow) var(--hn-ease-bloom)}:host([drawer]) dialog.closing .panel{animation:slide-out-end var(--hn-duration-normal) var(--hn-ease-exit) forwards}:host([drawer][placement="start"]) dialog{margin:0 auto 0 0}:host([drawer][placement="start"]) .panel{border-width:0 1px 0 0;border-radius:0 var(--hn-radius-2xl) var(--hn-radius-2xl) 0;animation-name:slide-in-start}:host([drawer][placement="start"]) dialog.closing .panel{animation-name:slide-out-start}:host([drawer][placement="top"]) dialog,:host([drawer][placement="bottom"]) dialog{width:100vw;height:auto;max-height:85dvh;margin:0 0 auto}:host([drawer][placement="bottom"]) dialog{margin:auto 0 0}:host([drawer][placement="top"]) .panel{border-width:0 0 1px;border-radius:0 0 var(--hn-radius-2xl) var(--hn-radius-2xl);animation-name:slide-in-top;max-height:85dvh}:host([drawer][placement="bottom"]) .panel{border-width:1px 0 0;border-radius:var(--hn-radius-2xl) var(--hn-radius-2xl) 0 0;animation-name:sheet-in;max-height:85dvh}:host([drawer][placement="top"]) dialog.closing .panel{animation-name:slide-out-top}:host([drawer][placement="bottom"]) dialog.closing .panel{animation-name:sheet-out}@keyframes fade-in{from{opacity:0}}@keyframes fade-out{to{opacity:0}}@keyframes bloom-in{from{opacity:0;transform:translateY(12px) scale(0.96)}}@keyframes bloom-out{to{opacity:0;transform:translateY(8px) scale(0.97)}}@keyframes sheet-in{from{transform:translateY(100%)}}@keyframes sheet-out{to{transform:translateY(100%)}}@keyframes slide-in-end{from{transform:translateX(100%)}}@keyframes slide-out-end{to{transform:translateX(100%)}}@keyframes slide-in-start{from{transform:translateX(-100%)}}@keyframes slide-out-start{to{transform:translateX(-100%)}}@keyframes slide-in-top{from{transform:translateY(-100%)}}@keyframes slide-out-top{to{transform:translateY(-100%)}}@keyframes shake{20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}@media (prefers-reduced-motion:reduce){.panel,dialog::backdrop{animation-duration:1ms !important}}`;

const html = /* html */ `
  <dialog part="dialog">
    <div class="panel" part="panel">
      <header class="header" part="header">
        <slot name="header"><h2 class="title" part="title"></h2></slot>
        <button class="close" part="close" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </header>
      <div class="body" part="body"><slot></slot></div>
      <footer class="footer" part="footer"><slot name="footer"></slot></footer>
    </div>
  </dialog>
`;

export type DialogCloseReason = 'escape' | 'backdrop' | 'close-button' | 'api';

export interface DialogRequestCloseDetail {
  reason: DialogCloseReason;
}

/**
 * Fenêtre modale accessible basée sur <dialog> (piège de focus, Échap, top layer).
 *
 * Attributs : open, label, size="sm|md|lg|xl|full", persistent (ni Échap ni clic
 * extérieur), no-close-button.
 * Événements : hn-open, hn-close, hn-request-close (annulable, detail.reason).
 */
export class HnDialog extends HanamiElement {
  static readonly tagName: string = 'hn-dialog';
  static get observedAttributes() {
    return ['open', 'label'];
  }

  protected dialog!: HTMLDialogElement;
  private titleEl!: HTMLElement;
  private closeReason: DialogCloseReason = 'api';
  private returnFocus: Element | null = null;
  private locked = false;
  private pointerDownOnBackdrop = false;

  get open(): boolean {
    return this.getBool('open');
  }
  set open(value: boolean) {
    this.setBool('open', value);
  }

  get label(): string {
    return this.getAttribute('label') ?? '';
  }
  set label(value: string) {
    this.setAttribute('label', value);
  }

  connectedCallback(): void {
    this.upgradeProperties('open', 'label');
    if (!this.shadowRoot) this.setup();
    this.syncLabel();
    if (this.open && !this.dialog.open) this.showDialog();
  }

  disconnectedCallback(): void {
    if (this.locked) {
      unlockScroll();
      this.locked = false;
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.dialog || oldValue === newValue) return;
    if (name === 'label') this.syncLabel();
    if (name === 'open') {
      if (this.open) this.showDialog();
      else void this.hideDialog();
    }
  }

  /** Ouvre la fenêtre. */
  show(): void {
    this.open = true;
  }

  /** Ferme la fenêtre (sans passer par hn-request-close). */
  hide(): void {
    this.closeReason = 'api';
    this.open = false;
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  /** Demande une fermeture : émet hn-request-close, annulable avec preventDefault(). */
  requestClose(reason: DialogCloseReason = 'api'): void {
    if (this.hasAttribute('persistent') && (reason === 'escape' || reason === 'backdrop')) {
      this.dialog.classList.remove('shake');
      void this.dialog.offsetWidth;
      this.dialog.classList.add('shake');
      return;
    }
    if (!this.emit<DialogRequestCloseDetail>('hn-request-close', { reason }, true)) return;
    this.closeReason = reason;
    this.open = false;
  }

  private setup(): void {
    const root = createShadow(this, css, html);
    this.dialog = root.querySelector('dialog')!;
    this.titleEl = root.querySelector('.title')!;
    this.titleEl.id = 'title';
    const closeButton = root.querySelector<HTMLButtonElement>('.close')!;
    closeButton.setAttribute('aria-label', t('close'));
    closeButton.addEventListener('click', () => this.requestClose('close-button'));

    this.dialog.addEventListener('cancel', event => {
      event.preventDefault();
      this.requestClose('escape');
    });
    // Clic sur le fond : le clic doit commencer ET finir sur le ::backdrop.
    this.dialog.addEventListener('pointerdown', event => {
      this.pointerDownOnBackdrop = event.target === this.dialog;
    });
    this.dialog.addEventListener('click', event => {
      if (event.target === this.dialog && this.pointerDownOnBackdrop) this.requestClose('backdrop');
      this.pointerDownOnBackdrop = false;
    });
    this.dialog.addEventListener('animationend', event => {
      if (event.animationName === 'shake') this.dialog.classList.remove('shake');
    });

    const headerSlot = root.querySelector<HTMLSlotElement>('slot[name="header"]')!;
    headerSlot.addEventListener('slotchange', () => this.syncLabel());
    const footerSlot = root.querySelector<HTMLSlotElement>('slot[name="footer"]')!;
    const footer = root.querySelector('.footer')!;
    const syncFooter = () => footer.classList.toggle('empty', footerSlot.assignedElements().length === 0);
    footerSlot.addEventListener('slotchange', syncFooter);
    syncFooter();
  }

  private syncLabel(): void {
    if (!this.dialog) return;
    this.titleEl.textContent = this.label;
    const header = this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="header"]')!.assignedElements();
    if (header.length) {
      this.dialog.removeAttribute('aria-labelledby');
      this.dialog.setAttribute('aria-label', this.label || header.map(el => el.textContent?.trim()).join(' '));
    } else if (this.label) {
      this.dialog.removeAttribute('aria-label');
      this.dialog.setAttribute('aria-labelledby', 'title');
    }
  }

  private showDialog(): void {
    if (this.dialog.open) {
      this.dialog.classList.remove('closing');
      return;
    }
    this.returnFocus = deepActiveElement();
    this.dialog.classList.remove('closing');
    this.dialog.showModal();
    bringToastsToFront();
    if (!this.locked) {
      lockScroll();
      this.locked = true;
    }
    // Focus : [autofocus] dans le contenu, sinon le premier élément focusable, sinon le panneau.
    const target = this.querySelector<HTMLElement>('[autofocus]') ?? getFocusable(this)[0];
    if (target) target.focus();
    this.closeReason = 'api';
    this.emit('hn-open');
  }

  private async hideDialog(): Promise<void> {
    if (!this.dialog.open) return;
    this.dialog.classList.add('closing');
    if (!prefersReducedMotion()) await animationsDone(this.dialog, 450);
    // Rouvert pendant l'animation de fermeture ?
    if (this.open) {
      this.dialog.classList.remove('closing');
      return;
    }
    this.dialog.close();
    this.dialog.classList.remove('closing');
    if (this.locked) {
      unlockScroll();
      this.locked = false;
    }
    const target = this.returnFocus;
    this.returnFocus = null;
    if (target instanceof HTMLElement && target.isConnected) target.focus();
    this.emit<DialogRequestCloseDetail>('hn-close', { reason: this.closeReason });
    this.closeReason = 'api';
  }
}

/**
 * Tiroir latéral : même API que <hn-dialog>, plus placement="start|end|top|bottom".
 */
export class HnDrawer extends HnDialog {
  static override readonly tagName = 'hn-drawer';

  override connectedCallback(): void {
    this.setAttribute('drawer', '');
    super.connectedCallback();
  }
}

define(HnDialog.tagName, HnDialog);
define(HnDrawer.tagName, HnDrawer);
